import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { HttpService } from "../../modules/shared/services/http.service";

// Types
export interface Favorite {
  id: string;
  userId: {
    id: string;
  };
  userIds: Array<{
    id: string;
  }>;
}

export interface FavoritesState {
  favorites: Favorite[];
  currentUserFavorite: Favorite | null;
  loading: boolean;
  error: string | null;
}

const initialState: FavoritesState = {
  favorites: [],
  currentUserFavorite: null,
  loading: false,
  error: null,
};

// Async thunks
export const createFavorite = createAsyncThunk(
  "favorites/createFavorite",
  async (userIds: string[], { rejectWithValue, getState }) => {
    try {
      const state = getState() as any;
      const userId = state.auth.user?.id;
      
      if (!userId) {
        return rejectWithValue("User ID not found");
      }
      
      const response = await HttpService.post("api/v1/favorites", {
        userId: {
          id: userId
        },
        userIds: userIds.map(id => ({ id })),
      });
      return response;
    } catch (error: any) {
      return rejectWithValue(error?.data?.message || "Failed to create favorite");
    }
  }
);

export const getAllFavorites = createAsyncThunk(
  "favorites/getAllFavorites",
  async (_, { rejectWithValue }) => {
    try {
      const response = await HttpService.get("api/v1/favorites");
      return response;
    } catch (error: any) {
      return rejectWithValue(error?.data?.message || "Failed to fetch favorites");
    }
  }
);

export const getFavoriteById = createAsyncThunk(
  "favorites/getFavoriteById",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await HttpService.get(`api/v1/favorites/${id}`);
      return response;
    } catch (error: any) {
      return rejectWithValue(error?.data?.message || "Failed to fetch favorite");
    }
  }
);

export const getFavoriteByUserId = createAsyncThunk(
  "favorites/getFavoriteByUserId",
  async (userId: string, { rejectWithValue }) => {
    try {
      const response = await HttpService.get(`api/v1/favorites/user/${userId}`);
      return response;
    } catch (error: any) {
      return rejectWithValue(error?.data?.message || "Failed to fetch user favorites");
    }
  }
);

export const updateFavorite = createAsyncThunk(
  "favorites/updateFavorite",
  async ({ id, userIds, userId }: { id: string; userIds: string[]; userId: string }, { rejectWithValue }) => {
    try {
      const response = await HttpService.patch(`api/v1/favorites/${id}`, {
        userId: {
          id: userId
        },
        userIds: userIds.map(id => ({ id })),
      });
      return response;
    } catch (error: any) {
      return rejectWithValue(error?.data?.message || "Failed to update favorite");
    }
  }
);

export const deleteFavorite = createAsyncThunk(
  "favorites/deleteFavorite",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await HttpService.deleteRecord(`api/v1/favorites/${id}`);
      return response;
    } catch (error: any) {
      return rejectWithValue(error?.data?.message || "Failed to delete favorite");
    }
  }
);

export const syncLocalFavorites = createAsyncThunk(
  "favorites/syncLocalFavorites",
  async (_, { dispatch, getState, rejectWithValue }) => {
    try {
      // Get local favorites from localStorage
      const localFavorites = localStorage.getItem("favoriteUserIds");
      const localUserIds = localFavorites ? JSON.parse(localFavorites) : [];
      
      if (localUserIds.length === 0) {
        return null;
      }

      // Get current user's favorites from backend
      const state = getState() as any;
      const currentUserFavorite = state.favorites.currentUserFavorite;

      if (currentUserFavorite) {
        // Update existing favorite
        const result = await dispatch(updateFavorite({
          id: currentUserFavorite.id,
          userIds: localUserIds,
          userId: currentUserFavorite.userId.id
        })).unwrap();
        
        // Clear local storage after successful sync
        localStorage.removeItem("favoriteUserIds");
        
        return result;
      } else {
        // Create new favorite
        const result = await dispatch(createFavorite(localUserIds)).unwrap();
        
        // Clear local storage after successful sync
        localStorage.removeItem("favoriteUserIds");
        
        return result;
      }
    } catch (error: any) {
      return rejectWithValue(error?.data?.message || "Failed to sync local favorites");
    }
  }
);

const favoritesSlice = createSlice({
  name: "favorites",
  initialState,
  reducers: {
    clearFavorites: (state) => {
      state.favorites = [];
      state.currentUserFavorite = null;
      state.error = null;
    },
    setCurrentUserFavorite: (state, action) => {
      state.currentUserFavorite = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Create Favorite
      .addCase(createFavorite.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createFavorite.fulfilled, (state, action) => {
        state.loading = false;
        state.currentUserFavorite = action.payload;
      })
      .addCase(createFavorite.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      // Get All Favorites
      .addCase(getAllFavorites.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllFavorites.fulfilled, (state, action) => {
        state.loading = false;
        state.favorites = action.payload.data;
      })
      .addCase(getAllFavorites.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      // Get Favorite By ID
      .addCase(getFavoriteById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getFavoriteById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentUserFavorite = action.payload;
      })
      .addCase(getFavoriteById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      // Get Favorite By User ID
      .addCase(getFavoriteByUserId.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getFavoriteByUserId.fulfilled, (state, action) => {
        state.loading = false;
        state.currentUserFavorite = action.payload;
      })
      .addCase(getFavoriteByUserId.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      // Update Favorite
      .addCase(updateFavorite.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateFavorite.fulfilled, (state, action) => {
        state.loading = false;
        state.currentUserFavorite = action.payload;
      })
      .addCase(updateFavorite.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      // Delete Favorite
      .addCase(deleteFavorite.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteFavorite.fulfilled, (state) => {
        state.loading = false;
        state.currentUserFavorite = null;
      })
      .addCase(deleteFavorite.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      // Sync Local Favorites
      .addCase(syncLocalFavorites.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(syncLocalFavorites.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload) {
          state.currentUserFavorite = action.payload;
        }
      })
      .addCase(syncLocalFavorites.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearFavorites, setCurrentUserFavorite } = favoritesSlice.actions;
export default favoritesSlice.reducer;
